/**
 * Notification Redux Selectors
 * Memoized selectors for accessing notification state
 */

import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { NotificationItem } from './types';

// Base selectors
export const selectNotificationState = (state: RootState) => state.notification;

export const selectNotifications = (state: RootState) => state.notification.notifications;

export const selectUnreadCount = (state: RootState) => state.notification.unreadCount;

export const selectPreferences = (state: RootState) => state.notification.preferences;

export const selectLoading = (state: RootState) => state.notification.loading;

export const selectError = (state: RootState) => state.notification.error;

export const selectPagination = (state: RootState) => state.notification.pagination;

export const selectLastFetched = (state: RootState) => state.notification.lastFetched;

// Derived selectors - Memoized to prevent unnecessary re-renders
export const selectUnreadNotifications = createSelector(
  [selectNotifications],
  (notifications): NotificationItem[] =>
    notifications.filter((notification) => !notification.isRead)
);

export const selectReadNotifications = createSelector(
  [selectNotifications],
  (notifications): NotificationItem[] =>
    notifications.filter((notification) => notification.isRead)
);

export const selectNotificationsByCategory = (category: string) =>
  createSelector([selectNotifications], (notifications): NotificationItem[] =>
    notifications.filter((notification) => notification.category === category)
  );

export const selectNotificationsByPriority = (priority: string) =>
  createSelector([selectNotifications], (notifications): NotificationItem[] =>
    notifications.filter((notification) => notification.priority === priority)
  );

export const selectHasUnread = (state: RootState): boolean => state.notification.unreadCount > 0;

export const selectIsNotificationsEnabled = (state: RootState): boolean =>
  state.notification.preferences?.enabled ?? true;

export const selectIsPushEnabled = (state: RootState): boolean =>
  state.notification.preferences?.channels?.push ?? true;

export const selectIsInAppEnabled = (state: RootState): boolean =>
  state.notification.preferences?.channels?.inApp ?? true;

export const selectIsEmailEnabled = (state: RootState): boolean =>
  state.notification.preferences?.channels?.email ?? false;

export const selectNotificationById = (notificationId: string) =>
  createSelector([selectNotifications], (notifications): NotificationItem | undefined =>
    notifications.find((notification) => notification._id === notificationId)
  );

export const selectUrgentNotifications = createSelector(
  [selectNotifications],
  (notifications): NotificationItem[] =>
    notifications.filter(
      (notification) => notification.priority === 'urgent' && !notification.isRead
    )
);

export const selectRecentNotifications = (limit: number = 5) =>
  createSelector([selectNotifications], (notifications): NotificationItem[] =>
    notifications.slice(0, limit)
  );

export const selectHasMoreNotifications = (state: RootState): boolean => {
  const { page, totalPages } = state.notification.pagination;
  return page < totalPages;
};

export const selectIsStale = (maxAge: number = 60000) => (state: RootState): boolean => {
  const { lastFetched } = state.notification;
  if (!lastFetched) return true;
  return Date.now() - lastFetched > maxAge;
};
